import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, Calendar, Clock, TrendingUp, ArrowRight, CheckCircle, AlertCircle, UserCheck, Fingerprint, ClipboardList, DollarSign } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'
import { format, startOfWeek, endOfWeek } from 'date-fns'
import { fr } from 'date-fns/locale'

interface ClockStats {
  shiftsToday: number
  absencesPending: number
  timesheetsToValidate: number
  presentCount: number
}

interface ShiftStats {
  totalEmployees: number
  shiftsThisWeek: number
  publishedShifts: number
  draftShifts: number
}

export default function DashboardRH() {
  const [shiftStats, setShiftStats] = useState<ShiftStats>({ totalEmployees: 0, shiftsThisWeek: 0, publishedShifts: 0, draftShifts: 0 })
  const [clockStats, setClockStats] = useState<ClockStats | null>(null)
  const [loading, setLoading] = useState(true)
  const { current: establishment } = useEstablishment()
  const user = JSON.parse(localStorage.getItem('user') || '{}')

  const today = new Date()
  const weekStart = startOfWeek(today, { weekStartsOn: 1 })
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 })

  useEffect(() => {
    async function load() {
      try {
        const [empRes, shiftRes, clockRes] = await Promise.all([
          api.get('/employees'),
          api.get(`/shifts?weekStart=${format(weekStart, 'yyyy-MM-dd')}`),
          // Badgeuse débranchée : l'API répond 403, on n'affiche simplement pas le bandeau
          establishment ? api.get(`/clock/stats?establishmentId=${establishment.id}`).catch(() => null) : Promise.resolve(null)
        ])
        const shifts = Array.isArray(shiftRes.data) ? shiftRes.data : []
        const employees = Array.isArray(empRes.data) ? empRes.data : []
        setShiftStats({
          totalEmployees: employees.length,
          shiftsThisWeek: shifts.length,
          publishedShifts: shifts.filter((s: any) => s.status === 'published').length,
          draftShifts: shifts.filter((s: any) => s.status === 'draft').length
        })
        if (clockRes) setClockStats(clockRes.data)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [establishment])

  function openKiosk() {
    if (establishment) {
      localStorage.setItem('kioskEstablishmentId', establishment.id)
    }
    window.open('/badge', '_blank')
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Bonjour, {user.firstName}</h1>
        <p className="text-gray-500 mt-1">
          Semaine du {format(weekStart, 'd MMMM', { locale: fr })} au {format(weekEnd, 'd MMMM yyyy', { locale: fr })}
        </p>
      </div>

      {/* Live presence banner */}
      {clockStats && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 mb-6 text-white flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-lg p-2.5">
              <UserCheck size={22} />
            </div>
            <div>
              <p className="text-2xl font-bold">{clockStats.presentCount} présent{clockStats.presentCount > 1 ? 's' : ''}</p>
              <p className="text-blue-100 text-sm">en ce moment • {clockStats.shiftsToday} shift{clockStats.shiftsToday > 1 ? 's' : ''} aujourd'hui</p>
            </div>
          </div>
          <div className="flex gap-6 text-right">
            {clockStats.absencesPending > 0 && (
              <div>
                <p className="text-xl font-bold text-amber-300">{clockStats.absencesPending}</p>
                <p className="text-blue-100 text-xs">absence{clockStats.absencesPending > 1 ? 's' : ''} en attente</p>
              </div>
            )}
            {clockStats.timesheetsToValidate > 0 && (
              <div>
                <p className="text-xl font-bold text-green-300">{clockStats.timesheetsToValidate}</p>
                <p className="text-blue-100 text-xs">feuille{clockStats.timesheetsToValidate > 1 ? 's' : ''} à valider</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Employés actifs', value: shiftStats.totalEmployees, icon: Users, color: 'bg-blue-500', link: '/employees' },
          { label: 'Shifts cette semaine', value: shiftStats.shiftsThisWeek, icon: Calendar, color: 'bg-indigo-500', link: '/planning' },
          { label: 'Shifts publiés', value: shiftStats.publishedShifts, icon: CheckCircle, color: 'bg-green-500', link: '/planning' },
          { label: 'Brouillons', value: shiftStats.draftShifts, icon: Clock, color: 'bg-amber-500', link: '/planning' },
        ].map(card => (
          <Link key={card.label} to={card.link} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className={`${card.color} p-2.5 rounded-lg`}>
                <card.icon className="text-white" size={18} />
              </div>
              <ArrowRight className="text-gray-300" size={16} />
            </div>
            {loading ? (
              <div className="h-8 bg-gray-100 rounded animate-pulse w-16 mb-1" />
            ) : (
              <p className="text-3xl font-bold text-gray-900">{card.value}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">{card.label}</p>
          </Link>
        ))}
      </div>

      {/* Quick actions + alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Accès rapide */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-blue-600" />
            Accès rapide
          </h2>
          <div className="space-y-1">
            <Link to="/planning" className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group">
              <div className="flex items-center gap-2.5"><Calendar size={16} className="text-indigo-500" /><span className="text-sm text-gray-700">Planning de la semaine</span></div>
              <ArrowRight size={15} className="text-gray-300 group-hover:text-blue-600 transition-colors" />
            </Link>
            <Link to="/absences" className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group">
              <div className="flex items-center gap-2.5"><AlertCircle size={16} className="text-amber-500" /><span className="text-sm text-gray-700">Demandes d'absence{clockStats?.absencesPending ? ` (${clockStats.absencesPending} en attente)` : ''}</span></div>
              <ArrowRight size={15} className="text-gray-300 group-hover:text-blue-600 transition-colors" />
            </Link>
            <Link to="/timesheets" className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group">
              <div className="flex items-center gap-2.5"><ClipboardList size={16} className="text-green-500" /><span className="text-sm text-gray-700">Validation des heures{clockStats?.timesheetsToValidate ? ` (${clockStats.timesheetsToValidate} à valider)` : ''}</span></div>
              <ArrowRight size={15} className="text-gray-300 group-hover:text-blue-600 transition-colors" />
            </Link>
            <Link to="/payroll" className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group">
              <div className="flex items-center gap-2.5"><DollarSign size={16} className="text-green-600" /><span className="text-sm text-gray-700">Rapport de paie du mois</span></div>
              <ArrowRight size={15} className="text-gray-300 group-hover:text-blue-600 transition-colors" />
            </Link>
          </div>
        </div>

        {/* Badgeuse */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Fingerprint size={18} className="text-purple-600" />
            Badgeuse
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Ouvrez la badgeuse en mode kiosque sur une tablette à l'entrée de votre établissement. Les employés pointent avec leur PIN.
          </p>
          <button onClick={openKiosk}
            className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-medium px-4 py-3 rounded-lg transition-colors">
            <Fingerprint size={18} />
            Ouvrir la badgeuse
          </button>
          <p className="text-xs text-gray-400 mt-3 text-center">
            S'ouvre dans un nouvel onglet • Définissez les PINs dans la fiche employé
          </p>

          {shiftStats.totalEmployees > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Link to="/employees" className="text-xs text-blue-600 hover:text-blue-800">
                Gérer les PINs des employés →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
